// @flow

import React from 'react';
import get from 'lodash.get';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';

import { Input } from 'dibs-elements/exports/Input';
import { Button } from 'dibs-elements/exports/Button';
import { Spinner } from 'dibs-elements/exports/Spinner';

import { commit } from '../mutations/UpdateProjectMutation';
import { isValidDateInput, dateInputToTimestamp, timestampToInputString } from '../helpers/date';
import { postProjectToFA } from '../faPoster';

import type { Environment } from 'react-relay';
import type { IntlShape } from 'react-intl';
import type { FormStatus, Project } from '../types.flow';

import styles from './styles/UpdateInstallDate.scss';

const messages = defineMessages({
    installDatePlaceholder: {
        id: 'dc.ProjectForms.UpdateInstallDate.installDatePlaceholder',
        defaultMessage: 'MM/DD/YYYY',
    },
});

export type UpdateInstallDateProps = {|
    firmId: ?string,
    onSuccess?: (payload: Project) => mixed,
    project: Project,
    relayEnvironment: Environment,
    source: string,
    userId: string,
|};

type UpdateInstallDateComponentProps = {|
    ...UpdateInstallDateProps,
    intl: IntlShape,
|};

type UpdateInstallDateState = {|
    formStatus: FormStatus,
    projectInstallDate: string,
|};

export class UpdateInstallDate extends React.Component<
    UpdateInstallDateComponentProps,
    UpdateInstallDateState
> {
    constructor(props: UpdateInstallDateComponentProps) {
        super(props);
        this.handleMutationError = this.handleMutationError.bind(this);
        this.handleMutationSuccess = this.handleMutationSuccess.bind(this);
        this.submit = this.submit.bind(this);
        this.onInstallDateUpdate = this.onInstallDateUpdate.bind(this);

        const timestamp = get(props, 'project.installDate');
        this.state = {
            formStatus: 'DEFAULT',
            projectInstallDate: timestampToInputString(timestamp),
        };
    }

    handleMutationError: (error: mixed) => void;
    handleMutationSuccess: (payload: Project) => void;
    onInstallDateUpdate: (e: SyntheticInputEvent<*>) => void;
    submit: (e: SyntheticInputEvent<*>) => void;

    handleMutationError(error: mixed) {
        this.setState({ formStatus: 'ERROR' });
        console.error('install date update failed:', error);
    }

    handleMutationSuccess(payload: Project) {
        const { firmId, onSuccess, source, userId } = this.props;
        const project = get(payload, 'updateProject.project');

        postProjectToFA({
            action: 'UPDATE',
            firmId,
            project,
            source,
            userId,
        });

        if (typeof onSuccess === 'function') {
            onSuccess(payload);
        }
    }

    onInstallDateUpdate(e: SyntheticInputEvent<*>) {
        this.setState({
            projectInstallDate: e.target.value,
        });
    }

    submit(e: SyntheticInputEvent<*>) {
        e.preventDefault();

        const { formStatus, projectInstallDate } = this.state;
        const { project, relayEnvironment } = this.props;

        if (formStatus === 'LOADING') {
            return;
        }

        this.setState({ formStatus: 'LOADING' });

        commit(relayEnvironment, {
            projectId: project.serviceId,
            installDate: dateInputToTimestamp(projectInstallDate),
            onCompleted: this.handleMutationSuccess,
            onError: this.handleMutationError,
        });
    }

    render() {
        const { intl } = this.props;
        const { formStatus, projectInstallDate } = this.state;
        const isValidInput = isValidDateInput(projectInstallDate);

        return (
            <form onSubmit={this.submit}>
                <div className={styles.formSection}>
                    <div className={styles.formSectionPrompt}>
                        <FormattedMessage
                            id="dc.ProjectForms.UpdateInstallDate.installDate"
                            defaultMessage="Install Date"
                        />
                    </div>
                    <Input
                        dataTn="project-install-date-input"
                        errorMessage={isValidInput ? null : ' '}
                        onChange={this.onInstallDateUpdate}
                        placeholder={intl.formatMessage(messages.installDatePlaceholder)}
                        value={projectInstallDate}
                    />
                </div>
                <Button
                    className={styles.submitButton}
                    dataTn="update-install-date-submit-button"
                    disabled={!isValidInput}
                >
                    {formStatus === 'LOADING' ? (
                        <Spinner containerClass={styles.spinnerContainer} />
                    ) : (
                        <FormattedMessage
                            id="dc.ProjectForms.UpdateInstallDate.submitCta"
                            defaultMessage="Done"
                        />
                    )}
                </Button>
                {formStatus === 'ERROR' && (
                    <div className={styles.errorMessage}>
                        <FormattedMessage
                            id="dc.ProjectForms.UpdateInstallDate.errorMessage"
                            defaultMessage="An error occurred. Project install date could not be updated."
                        />
                    </div>
                )}
            </form>
        );
    }
}

export default injectIntl(UpdateInstallDate);
